/**
 * Merfast theme JS (mf-theme.js) — owner: mf-theme-core. ES module loaded from layout/theme.liquid.
 * Only touches `[data-mf-*]` hooks. Public API on `window.MF`. Docs: theme-work/CSS-API.md
 *
 * Cart events (Horizon 4.1.4): the theme listens to `StandardEvents.cartLinesUpdate`
 * ('shopify:cart:lines-update') from `@shopify/events` (importmap in snippets/scripts.liquid).
 * We dispatch the same `CartLinesUpdateEvent` so Horizon's cart drawer / cart icon refresh themselves,
 * then emit our own `mf:cart:update` with the raw /cart.js payload for mf-* consumers.
 */
let SE = null;
try { SE = await import('@shopify/events'); } catch (e) { /* CDN blocked → fallback CustomEvent */ }

const CART_EVENT = (SE && SE.StandardEvents && SE.StandardEvents.cartLinesUpdate) || 'shopify:cart:lines-update';
const routes = () => (window.Theme && window.Theme.routes) || {};
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const on = (type, sel, fn, opts) => document.addEventListener(type, (e) => { const t = e.target instanceof Element ? e.target.closest(sel) : null; if (t) fn(e, t); }, opts);

const MF = (window.MF = window.MF || {});
MF.version = '1.0.0';
MF.events = {
  cartUpdate: 'mf:cart:update',           // detail: { cart } raw /cart.js json (or { cart:null, normalized } when relayed from Horizon)
  cartAdd: 'mf:cart:add',                 // detail: { items, cart }
  cartError: 'mf:cart:error',             // detail: { message, response }
  quickviewOpen: 'mf:quickview:open',     // detail: { handle, root }
  quickviewClose: 'mf:quickview:close',
  offcanvasToggle: 'mf:offcanvas:toggle', // detail: { id, open }
  gridChange: 'mf:grid:change',           // detail: { cols, target }
  carouselChange: 'mf:carousel:change',   // detail: { index, root }
  horizonCartLinesUpdate: CART_EVENT,
};
const emit = (name, detail = {}, target = document) => target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

/* ---------------- money ---------------- */
const moneyFormat = () => { const el = $('[data-mf-money-format]'); return (el && el.getAttribute('data-mf-money-format')) || '${{amount}}'; };
function formatMoney(cents, format = moneyFormat()) {
  const n = Number(cents) / 100;
  const fixed = (d, thousands = ',', decimal = '.') => {
    const parts = n.toFixed(d).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };
  const map = {
    amount: fixed(2), amount_no_decimals: fixed(0),
    amount_with_comma_separator: fixed(2, '.', ','), amount_no_decimals_with_comma_separator: fixed(0, '.', ','),
    amount_with_space_separator: fixed(2, ' ', ','), amount_no_decimals_with_space_separator: fixed(0, ' ', ','),
    amount_with_apostrophe_separator: fixed(2, "'", '.'),
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (map[k] !== undefined ? map[k] : map.amount)).replace(/<[^>]+>/g, '');
}
MF.formatMoney = formatMoney;

/* ---------------- cart state ---------------- */
function updateCartUI({ count, totalCents, totalFormatted }) {
  if (count !== undefined) $$('[data-mf-cart-count]').forEach((el) => { el.textContent = String(count); el.classList.toggle('is-empty', !count); });
  if (totalFormatted !== undefined) $$('[data-mf-cart-total]').forEach((el) => { el.textContent = totalFormatted; });
  else if (totalCents !== undefined) $$('[data-mf-cart-total]').forEach((el) => { el.textContent = formatMoney(totalCents); });
}
async function refreshCart() {
  const r = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  const cart = await r.json();
  updateCartUI({ count: cart.item_count, totalCents: cart.total_price });
  emit(MF.events.cartUpdate, { cart });
  return cart;
}
MF.refreshCart = refreshCart;

// Keep header count/total in sync with Horizon's own product forms / cart drawer changes.
document.addEventListener(CART_EVENT, (event) => {
  const p = event.promise;
  if (!p) { refreshCart().catch(() => {}); return; }
  p.then(({ cart }) => {
    if (!cart) return refreshCart();
    const amount = Number(cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.amount);
    updateCartUI({ count: cart.totalQuantity || 0, totalCents: Number.isFinite(amount) ? Math.round(amount * 100) : undefined });
    emit(MF.events.cartUpdate, { cart: null, normalized: cart });
    return null;
  }).catch(() => {});
});
window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart().catch(() => {}); });

/* ---------------- cart drawer / search modal (reuse Horizon components) ---------------- */
async function openCart() {
  const drawer = $('#cart-drawer');
  if (!drawer) { location.href = routes().cart_url || '/cart'; return false; }
  try { await customElements.whenDefined('theme-drawer'); } catch (e) { /* noop */ }
  if (typeof drawer.open === 'function') { drawer.open(); return true; }
  location.href = routes().cart_url || '/cart';
  return false;
}
MF.openCart = openCart;
on('click', '[data-mf-cart-open]', (e) => { if ($('#cart-drawer')) { e.preventDefault(); openCart(); } });

async function openSearch() {
  const modal = $('#search-modal');
  if (!modal) { location.href = routes().search_url || '/search'; return false; }
  try { await customElements.whenDefined('dialog-component'); } catch (e) { /* noop */ }
  if (typeof modal.showDialog === 'function') { modal.showDialog(); return true; }
  location.href = routes().search_url || '/search';
  return false;
}
MF.openSearch = openSearch;
on('click', '[data-mf-search-open]', (e) => { e.preventDefault(); openSearch(); });

/* ---------------- add to cart ---------------- */
function cartSectionIds() {
  return $$('cart-items-component[data-section-id]').map((el) => el.dataset.sectionId).filter(Boolean);
}
function showFormError(source, message) {
  const root = source instanceof HTMLFormElement ? source : null;
  const el = root ? (root.querySelector('[data-mf-add-error]') || (root.parentElement && root.parentElement.querySelector('[data-mf-add-error]'))) : null;
  if (el) { el.textContent = message; el.hidden = !message; }
  else if (message) console.warn('[mf-theme] add to cart:', message);
}
/**
 * @param {HTMLFormElement|{id:string|number, quantity?:number, properties?:object, selling_plan?:string}} source
 * @param {{openDrawer?:boolean, button?:HTMLElement}} opts
 */
async function addToCart(source, opts = {}) {
  const isForm = source instanceof HTMLFormElement;
  const fd = isForm ? new FormData(source) : new FormData();
  if (!isForm) {
    fd.set('id', String(source.id));
    fd.set('quantity', String(source.quantity || 1));
    if (source.selling_plan) fd.set('selling_plan', String(source.selling_plan));
    if (source.properties) for (const [k, v] of Object.entries(source.properties)) fd.set(`properties[${k}]`, String(v));
  }
  if (!fd.get('quantity')) fd.set('quantity', '1');
  const sections = cartSectionIds();
  if (sections.length) fd.set('sections', sections.join(','));
  fd.set('sections_url', location.pathname);
  const quantity = Number(fd.get('quantity')) || 1;
  const merchandiseId = String(fd.get('id') || '');
  const button = opts.button;
  if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }

  // Horizon-compatible event (cart-icon, cart-drawer, cart-items-component listen to it)
  let deferred = null;
  const target = isForm ? source : document;
  if (SE && SE.CartLinesUpdateEvent) {
    deferred = SE.CartLinesUpdateEvent.createPromise();
    target.dispatchEvent(new SE.CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId, quantity }], promise: deferred.promise }));
  }
  try {
    const res = await fetch(routes().cart_add_url || '/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
    const json = await res.json();
    if (!res.ok || json.status) {
      const message = json.description || json.message || 'Could not add to cart';
      const cart = await refreshCart().catch(() => null);
      if (deferred) deferred.resolve({ cart: cart && SE ? SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null, detail: { didError: true, source: 'mf-theme', itemCount: quantity } });
      emit(MF.events.cartError, { message, response: json }, target);
      showFormError(source, message);
      return { ok: false, message, response: json };
    }
    const cart = await refreshCart();
    const items = json.items || [json];
    if (deferred) deferred.resolve({ cart: SE ? SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null, detail: { didError: false, source: 'mf-theme', sections: json.sections, items, itemCount: quantity, productId: items[0] && items[0].product_id } });
    emit(MF.events.cartAdd, { items, cart }, target);
    showFormError(source, '');
    if (opts.openDrawer !== false) { closeModal(); openCart(); } // merfast.com opens the cart widget after add
    return { ok: true, items, cart };
  } catch (err) {
    if (deferred) deferred.reject(err);
    // hard fallback: let the browser do a normal cart flow
    if (isForm) { source.removeAttribute('data-mf-add-to-cart'); source.submit(); } else { location.href = routes().cart_url || '/cart'; }
    return { ok: false, message: String(err) };
  } finally {
    if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
  }
}
MF.addToCart = addToCart;
document.addEventListener('submit', (e) => {
  const form = e.target instanceof Element ? e.target.closest('form[data-mf-add-to-cart]') : null;
  if (!form) return;
  e.preventDefault();
  addToCart(form, { button: form.querySelector('[type="submit"]') || undefined });
});
on('click', 'button[data-mf-add-to-cart], a[data-mf-add-to-cart]', (e, btn) => {
  if (btn.closest('form[data-mf-add-to-cart]')) return; // form submit handles it
  const id = btn.getAttribute('data-mf-add-to-cart') || btn.dataset.variantId;
  if (!id) return;
  e.preventDefault();
  const scope = btn.closest('[data-mf-qty-scope]');
  const qtyInput = scope ? scope.querySelector('input[name="quantity"]') : null;
  addToCart({ id, quantity: Number(qtyInput && qtyInput.value) || Number(btn.dataset.quantity) || 1 }, { button: btn });
});

/* ---------------- quantity ± ---------------- */
function qtyInputFor(btn) {
  const scope = btn.closest('[data-mf-qty]') || btn.closest('[data-mf-qty-scope]') || btn.parentElement;
  return scope ? scope.querySelector('input[type="number"], input[name="quantity"], input[name="updates[]"]') : null;
}
on('click', '[data-mf-qty-plus], [data-mf-qty-minus]', (e, btn) => {
  e.preventDefault();
  const input = qtyInputFor(btn);
  if (!input) return;
  const step = Number(input.step) || 1;
  const min = input.min !== '' ? Number(input.min) : 0;
  const max = input.max !== '' ? Number(input.max) : Infinity;
  let v = Number(input.value) || 0;
  v = btn.hasAttribute('data-mf-qty-plus') ? v + step : v - step;
  v = Math.min(max, Math.max(min, v));
  input.value = String(v);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------------- off-canvas ---------------- */
let backdrop = null;
const openStack = [];
function ensureBackdrop() {
  if (backdrop) return backdrop;
  backdrop = document.createElement('div');
  backdrop.className = 'mf-backdrop';
  backdrop.addEventListener('click', () => { closeOffcanvas(); closeModal(); });
  document.body.appendChild(backdrop);
  return backdrop;
}
function syncBackdrop() {
  const anyOpen = openStack.length > 0 || !!$('.mf-modal.is-open');
  ensureBackdrop().classList.toggle('is-open', openStack.length > 0);
  document.body.classList.toggle('mf-scroll-lock', anyOpen);
}
function openOffcanvas(id) {
  const el = document.getElementById(id);
  if (!el) return false;
  el.classList.add('is-open');
  el.setAttribute('aria-hidden', 'false');
  if (!openStack.includes(el)) openStack.push(el);
  syncBackdrop();
  const focusTarget = el.querySelector('[data-mf-offcanvas-close]') || el;
  if (typeof focusTarget.focus === 'function') focusTarget.focus({ preventScroll: true });
  emit(MF.events.offcanvasToggle, { id, open: true }, el);
  $$(`[data-mf-offcanvas-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
  return true;
}
function closeOffcanvas(id) {
  const targets = id ? [document.getElementById(id)].filter(Boolean) : openStack.slice();
  targets.forEach((el) => {
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    const i = openStack.indexOf(el); if (i > -1) openStack.splice(i, 1);
    emit(MF.events.offcanvasToggle, { id: el.id, open: false }, el);
    $$(`[data-mf-offcanvas-open="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
  });
  syncBackdrop();
}
MF.openOffcanvas = openOffcanvas; MF.closeOffcanvas = closeOffcanvas;
on('click', '[data-mf-offcanvas-open]', (e, btn) => { e.preventDefault(); const id = btn.getAttribute('data-mf-offcanvas-open'); const el = document.getElementById(id); if (el && el.classList.contains('is-open')) closeOffcanvas(id); else openOffcanvas(id); });
on('click', '[data-mf-offcanvas-close]', (e, btn) => { e.preventDefault(); const oc = btn.closest('.mf-offcanvas'); closeOffcanvas(btn.getAttribute('data-mf-offcanvas-close') || (oc && oc.id)); });

/* ---------------- modal / quick view (Section Rendering API) ---------------- */
function ensureModal() {
  let modal = $('#mf-quickview-modal');
  if (modal) return modal;
  modal = document.createElement('div');
  modal.id = 'mf-quickview-modal';
  modal.className = 'mf-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = '<div class="mf-modal__backdrop" data-mf-modal-close></div><div class="mf-modal__dialog"><button type="button" class="mf-icon-btn mf-modal__close" data-mf-modal-close aria-label="Close"><svg class="mf-icon" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button><div class="mf-modal__body" data-mf-modal-body></div></div>';
  document.body.appendChild(modal);
  return modal;
}
function closeModal() {
  const modal = $('#mf-quickview-modal');
  if (!modal || !modal.classList.contains('is-open')) return;
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  syncBackdrop();
  emit(MF.events.quickviewClose, {}, modal);
}
const qvCache = new Map();
async function openQuickView(handle, sectionId = 'mf-quickview') {
  const modal = ensureModal();
  const body = modal.querySelector('[data-mf-modal-body]');
  modal.classList.add('is-open', 'is-loading');
  modal.setAttribute('aria-hidden', 'false');
  syncBackdrop();
  try {
    let html = qvCache.get(handle);
    if (!html) {
      const root = (routes().root_url || '/').replace(/\/$/, '');
      const res = await fetch(`${root}/products/${handle}?section_id=${encodeURIComponent(sectionId)}`, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`quick view ${res.status}`);
      html = await res.text();
      qvCache.set(handle, html);
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inner = doc.querySelector('.shopify-section') || doc.body;
    body.innerHTML = inner.innerHTML;
    // Section Rendering API returns inline <script> inert — re-create them so the section can boot itself
    body.querySelectorAll('script').forEach((old) => { const s = document.createElement('script'); Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value)); s.textContent = old.textContent; old.replaceWith(s); });
    initCarousels(body); initHoverImages(body);
    emit(MF.events.quickviewOpen, { handle, root: body }, modal);
    const closeBtn = modal.querySelector('.mf-modal__close'); if (closeBtn) closeBtn.focus({ preventScroll: true });
  } catch (err) {
    console.warn('[mf-theme] quick view failed, redirecting', err);
    location.href = `/products/${handle}`;
  } finally {
    modal.classList.remove('is-loading');
  }
}
MF.openQuickView = openQuickView; MF.closeQuickView = closeModal;
on('click', '[data-mf-quickview]', (e, btn) => { const h = btn.getAttribute('data-mf-quickview'); if (!h) return; e.preventDefault(); openQuickView(h, btn.getAttribute('data-mf-quickview-section') || 'mf-quickview'); });
on('click', '[data-mf-modal-close]', (e) => { e.preventDefault(); closeModal(); });
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeModal(); closeOffcanvas(); } });

/* ---------------- carousel ---------------- */
function initCarousels(root = document) {
  $$('[data-mf-carousel]', root).forEach((car) => {
    if (car.dataset.mfCarouselReady) return;
    const track = car.querySelector('[data-mf-carousel-track]');
    if (!track) return;
    car.dataset.mfCarouselReady = '1';
    const prev = car.querySelector('[data-mf-carousel-prev]');
    const next = car.querySelector('[data-mf-carousel-next]');
    const step = () => { const first = track.firstElementChild; const cs = getComputedStyle(track); const gap = parseFloat(cs.columnGap || cs.gap) || 0; return first ? first.getBoundingClientRect().width + gap : track.clientWidth; };
    const atStart = () => track.scrollLeft <= 2;
    const atEnd = () => track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    const loop = car.hasAttribute('data-mf-carousel-loop');
    const update = () => {
      const overflow = track.scrollWidth > track.clientWidth + 2;
      car.classList.toggle('has-overflow', overflow);
      if (prev) prev.disabled = !overflow || (!loop && atStart());
      if (next) next.disabled = !overflow || (!loop && atEnd());
      const idx = Math.round(track.scrollLeft / Math.max(1, step()));
      if (car.dataset.mfIndex !== String(idx)) { car.dataset.mfIndex = String(idx); emit(MF.events.carouselChange, { index: idx, root: car }, car); }
      car.querySelectorAll('.mf-carousel__dot').forEach((d, i) => d.classList.toggle('is-active', i === idx));
    };
    const go = (dir) => {
      if (loop && dir > 0 && atEnd()) { track.scrollTo({ left: 0, behavior: 'smooth' }); return; }
      if (loop && dir < 0 && atStart()) { track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' }); return; }
      track.scrollBy({ left: dir * step() * (Number(car.dataset.mfCarouselStep) || 1), behavior: 'smooth' });
    };
    if (prev) prev.addEventListener('click', (e) => { e.preventDefault(); go(-1); });
    if (next) next.addEventListener('click', (e) => { e.preventDefault(); go(1); });
    let raf = 0;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
    new ResizeObserver(update).observe(track);
    track.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') { e.preventDefault(); go(1); } if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); } });
    if (!track.hasAttribute('tabindex')) track.setAttribute('tabindex', '0');
    const auto = Number(car.dataset.mfCarouselAutoplay);
    if (auto > 0) {
      let timer = setInterval(() => go(1), auto);
      const stop = () => clearInterval(timer);
      const start = () => { stop(); timer = setInterval(() => go(1), auto); };
      car.addEventListener('pointerenter', stop); car.addEventListener('pointerleave', start);
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    }
    car.mfCarousel = { go, update, next: () => go(1), prev: () => go(-1), goTo: (i) => track.scrollTo({ left: i * step(), behavior: 'smooth' }) };
    update();
  });
}
MF.initCarousels = initCarousels;

/* ---------------- hover image ---------------- */
function initHoverImages(root = document) {
  $$('[data-mf-hover-img]', root).forEach((wrap) => {
    if (wrap.dataset.mfHoverReady) return;
    wrap.dataset.mfHoverReady = '1';
    const alt = wrap.querySelector('.mf-hover-img__alt, img[data-src]');
    const src = (alt && alt.getAttribute('data-src')) || wrap.getAttribute('data-mf-hover-img');
    if (!alt || !src) return;
    const load = () => { if (!alt.getAttribute('src')) { alt.setAttribute('src', src); const ss = alt.getAttribute('data-srcset'); if (ss) alt.setAttribute('srcset', ss); } };
    wrap.addEventListener('pointerenter', load, { once: true, passive: true });
    wrap.addEventListener('touchstart', load, { once: true, passive: true });
    wrap.addEventListener('focusin', load, { once: true });
  });
}
MF.initHoverImages = initHoverImages;

/* ---------------- grid toggle ---------------- */
const GRID_KEY = 'mf:grid-cols';
function applyGrid(cols, target) {
  if (!target) return;
  target.style.setProperty('--mf-cols', String(cols));
  target.setAttribute('data-mf-cols', String(cols));
  $$('[data-mf-grid-toggle]').forEach((b) => b.classList.toggle('is-active', b.getAttribute('data-mf-grid-toggle') === String(cols)));
  emit(MF.events.gridChange, { cols, target }, target);
}
function gridTarget(btn) {
  const sel = btn.getAttribute('data-mf-grid-target');
  return sel ? $(sel) : ($('[data-mf-grid]') || $('.mf-grid'));
}
on('click', '[data-mf-grid-toggle]', (e, btn) => {
  e.preventDefault();
  const cols = Number(btn.getAttribute('data-mf-grid-toggle'));
  applyGrid(cols, gridTarget(btn));
  try { localStorage.setItem(GRID_KEY, String(cols)); } catch (err) { /* private mode */ }
});
function restoreGrid() {
  let cols = 0;
  try { cols = Number(localStorage.getItem(GRID_KEY)); } catch (err) { /* noop */ }
  if (!cols) return;
  const btn = $(`[data-mf-grid-toggle="${cols}"]`);
  if (!btn || getComputedStyle(btn).display === 'none') return;
  applyGrid(cols, gridTarget(btn));
}

/* ---------------- share ---------------- */
on('click', '[data-mf-share]', (e, btn) => {
  const net = btn.getAttribute('data-mf-share') || 'native';
  const url = btn.getAttribute('data-mf-share-url') || location.href;
  const title = btn.getAttribute('data-mf-share-title') || document.title;
  const image = btn.getAttribute('data-mf-share-image') || '';
  const u = encodeURIComponent(url), t = encodeURIComponent(title), i = encodeURIComponent(image);
  const map = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    x: `https://x.com/intent/post?url=${u}&text=${t}`,
    twitter: `https://x.com/intent/post?url=${u}&text=${t}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${u}&media=${i}&description=${t}`,
    email: `mailto:?subject=${t}&body=${u}`,
  };
  e.preventDefault();
  if (net === 'native' && navigator.share) { navigator.share({ title, url }).catch(() => {}); return; }
  if (net === 'copy') { if (navigator.clipboard) navigator.clipboard.writeText(url); btn.classList.add('is-copied'); setTimeout(() => btn.classList.remove('is-copied'), 1500); return; }
  const href = map[net] || url;
  if (net === 'email') { location.href = href; return; }
  window.open(href, 'mf-share', 'width=640,height=520,menubar=no,toolbar=no,resizable=yes,scrollbars=yes');
});

/* ---------------- header sticky + heights ---------------- */
function initHeader() {
  const section = $('.mf-header-section');
  const header = $('[data-mf-header]');
  if (!section || !header) return;
  const setVars = () => {
    const h = header.offsetHeight;
    document.documentElement.style.setProperty('--mf-header-offset', `${h}px`);
    // Horizon fallbacks: theme.liquid only measures these when #header-component exists (Horizon header is disabled)
    if (!document.body.style.getPropertyValue('--header-height')) document.body.style.setProperty('--header-height', `${h}px`);
    const group = $('#header-group');
    if (group && !document.body.style.getPropertyValue('--header-group-height')) {
      let total = 0; for (const el of group.children) total += el.offsetHeight; document.body.style.setProperty('--header-group-height', `${total}px`);
    }
  };
  setVars();
  new ResizeObserver(setVars).observe(header);
  const sentinel = document.createElement('div');
  sentinel.setAttribute('data-mf-sticky-sentinel', '');
  sentinel.style.cssText = 'position:absolute;height:1px;width:1px;pointer-events:none;';
  if (section.parentElement) section.parentElement.insertBefore(sentinel, section);
  new IntersectionObserver(([entry]) => section.classList.toggle('is-stuck', !entry.isIntersecting), { rootMargin: '-1px 0px 0px 0px', threshold: 0 }).observe(sentinel);
}

/* ---------------- back to top ---------------- */
function initBackTop() {
  const btn = $('[data-mf-backtop]');
  if (!btn) return;
  const toggle = () => btn.classList.toggle('is-visible', window.scrollY > 400);
  window.addEventListener('scroll', toggle, { passive: true });
  btn.addEventListener('click', (e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); });
  toggle();
}

/* ---------------- boot ---------------- */
function boot() {
  $$('[data-mf-offcanvas]').forEach((el) => { if (!el.hasAttribute('aria-hidden')) el.setAttribute('aria-hidden', 'true'); });
  initHeader();
  initCarousels();
  initHoverImages();
  initBackTop();
  restoreGrid();
  // theme editor / Section Rendering re-renders
  document.addEventListener('shopify:section:load', (e) => { initCarousels(e.target); initHoverImages(e.target); });
  MF.ready = true;
  emit('mf:ready', { MF });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
